import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Typography,
  IconButton,
  Divider,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const UploadFileModal = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    files: [],
    description: "",
    usedBy: [],
  });

  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);

    // Add files to formData
    const newFiles = selectedFiles.map((file) => ({
      file,
      name: file.name,
      size: formatFileSize(file.size),
      type: getFileType(file.name),
      status: "pending",
    }));

    setFormData({
      ...formData,
      files: [...formData.files, ...newFiles],
    });

    // Simulate upload progress
    setIsUploading(true);
    selectedFiles.forEach((file, index) => {
      simulateUpload(file.name);
    });
  };

  const simulateUpload = (fileName) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: progress,
      }));

      if (progress >= 100) {
        clearInterval(interval);
        // Mark file as uploaded
        setFormData((prev) => ({
          ...prev,
          files: prev.files.map((f) =>
            f.name === fileName ? { ...f, status: "uploaded" } : f
          ),
        }));
      }
    }, 200);
  };

  const handleRemoveFile = (fileName) => {
    setFormData({
      ...formData,
      files: formData.files.filter((f) => f.name !== fileName),
    });
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileType = (fileName) => {
    const ext = fileName.split(".").pop().toUpperCase();
    return ext;
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "PDF":
        return <PictureAsPdfIcon sx={{ color: "#D32F2F" }} />;
      case "DOCX":
      case "DOC":
        return <ArticleIcon sx={{ color: "#1976D2" }} />;
      default:
        return <DescriptionIcon sx={{ color: "#666" }} />;
    }
  };

  const handleSubmit = () => {
    const uploadedFiles = formData.files.filter((f) => f.status === "uploaded");
    if (uploadedFiles.length > 0) {
      onSave(formData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      files: [],
      description: "",
      usedBy: [],
    });
    setUploadProgress({});
    setIsUploading(false);
    onClose();
  };

  useEffect(() => {
    const allUploaded = formData.files.every((f) => f.status === "uploaded");
    if (allUploaded && formData.files.length > 0) {
      setIsUploading(false);
    }
  }, [formData.files]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            Upload Files to Knowledge Base
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Alert severity="info">
            Upload documents to enhance your AI assistant's knowledge. Supported formats: PDF, DOCX, TXT, CSV, MD
          </Alert>

          {/* File Upload Area */}
          <Card sx={{ border: "2px dashed #E0E0E0", backgroundColor: "#F9FAFB" }}>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <CloudUploadIcon sx={{ fontSize: 64, color: "primary.main", opacity: 0.6 }} />
                <Typography variant="h6" fontWeight={600}>
                  Drop files here or click to browse
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Maximum file size: 10MB per file
                  <br />
                  Supported: PDF, DOCX, TXT, CSV, MD
                </Typography>
                <input
                  accept=".pdf,.doc,.docx,.txt,.csv,.md"
                  style={{ display: "none" }}
                  id="file-upload-input"
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                />
                <label htmlFor="file-upload-input">
                  <CustomButton
                    icon={<CloudUploadIcon />}
                    iconPosition="left"
                    text="Select Files"
                    component="span"
                  />
                </label>
              </Box>
            </CardContent>
          </Card>

          {/* Uploaded Files List */}
          {formData.files.length > 0 && (
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Files ({formData.files.length})
              </Typography>
              <List sx={{ backgroundColor: "#F9FAFB", borderRadius: 2, p: 1 }}>
                {formData.files.map((file, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: 1,
                      mb: 1,
                      border: "1px solid #E0E0E0",
                    }}
                    secondaryAction={
                      file.status === "uploaded" ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CheckCircleIcon color="success" />
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveFile(file.name)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveFile(file.name)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemIcon>{getFileIcon(file.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {file.name}
                          </Typography>
                          <Chip label={file.type} size="small" sx={{ height: 20 }} />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {file.status === "pending" ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Uploading... {uploadProgress[file.name] || 0}%
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={uploadProgress[file.name] || 0}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="caption" color="success.main">
                              {file.size} - Uploaded successfully
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {formData.files.length > 0 && (
            <>
              <Divider />

              {/* Additional Information */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Additional Information
                </Typography>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Add notes about these files..."
                  sx={{ mt: 2 }}
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton text="Cancel" variant="outlined" onClick={handleClose} />
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text="Upload & Index Files"
          onClick={handleSubmit}
          disabled={isUploading || formData.files.length === 0}
        />
      </DialogActions>
    </Dialog>
  );
};

export default UploadFileModal;
